import { compose } from "redux";
import { Set } from "immutable";
import { connect } from "react-redux";
import { Grid } from "@material-ui/core";
import Delete from "@material-ui/icons/Delete";
import React, { useEffect, useMemo } from "react";

import {
  facilityTypeToString,
  facilityStatusToString,
} from "features/planner/planner.mappers";

import {
  ACTIONS,
  GROUP_FORM,
  GROUP_TYPES,
  SORT_DIRECTION,
} from "features/planner/planner.constants";

import {
  initializeEditGroup,
  editLocationGroupOnSubmit,
  deleteGroup,
  createLocationGroupOnSubmit,
  initializeEditLocationGroup,
  asyncValidateLocationGroup,
  getGroups,
  getFields,
  getStationaryAndMobileFacilities,
} from "features/planner/planner.actions";

import {
  getSummarizedDataStatesFromState,
  getCurrentUserAppRolesFromState,
} from "app/app.selectors";

import CrudBasePage from "app/components/CrudBasePage";
import MuiOilrigIcon from "app/components/MuiOilrigIcon";
import { getAccessControlButton } from "utils/component.util";
import { getModifiedByValue, hasRequiredAccessLevel } from "utils/app.util";
import { getLocationGroupsFromState } from "features/planner/planner.selectors";
import LocationGroupForm from "features/planner/settings/locations/components/LocationGroupForm";

const SettingsLocationGroupsContainer = ({
  breadcrumb,
  dataState,
  locationGroups,
  dispatchEditGroupOnSubmit,
  dispatchCreateLocationGroupOnSubmit,
  dispatchInitializeEditLocationGroup,
  crudAccessLevel,
  currentUserAppRoles,
  dispatchOnLoad,
  dispatchGetFields,
  dispatchGetFacilities,
  dispatchDeleteGroup,
}) => {
  useEffect(() => {
    dispatchOnLoad();
    dispatchGetFields();
    dispatchGetFacilities();
  }, [dispatchOnLoad, dispatchGetFields, dispatchGetFacilities]);

  const columns = useMemo(
    () => [
      {
        xs: true,
        title: "Name",
        getSortProperty: item => item.get("name"),
      },
      {
        xs: true,
        title: "Type",
        getSortProperty: item =>
          facilityTypeToString(item.getIn(["facility", "type"])),
      },
      {
        xs: true,
        title: "Field",
        getSortProperty: item => item.get("fieldName"),
      },
      {
        xs: true,
        title: "Operator",
        getSortProperty: item => item.getIn(["facility", "operator"]),
      },
      {
        xs: true,
        title: "Status",
        getSortProperty: item =>
          facilityStatusToString(item.getIn(["facility", "status"])),
      },
      {
        xs: true,
        component: Grid,
        zeroMinWidth: true,
        title: "Modified by",
        getSortProperty: item => item.get("modifiedBy"),
        getValue: getModifiedByValue,
      },
    ],
    [],
  );

  const actions = useMemo(
    () => [
      getAccessControlButton(
        crudAccessLevel.delete,
        dispatchDeleteGroup,
        <Delete fontSize="small" />,
        "Delete",
      ),
    ],
    [crudAccessLevel, dispatchDeleteGroup],
  );

  const groupForm = {
    form: GROUP_FORM.ID,
    initialValues: {
      [GROUP_FORM.TYPE]: GROUP_TYPES.LOCATION_GROUP,
    },
  };

  const createGroupForm = {
    ...groupForm,
    asyncValidate: asyncValidateLocationGroup,
    asyncChangeFields: [GROUP_FORM.FACILITY_ID],
    shouldAsyncValidate: () => true, // always async validate facility on change
  };

  return (
    <CrudBasePage
      Icon={MuiOilrigIcon}
      title={breadcrumb}
      items={locationGroups}
      displayAddButton={hasRequiredAccessLevel(
        crudAccessLevel.create,
        currentUserAppRoles,
      )}
      displaySearchField
      editEntityForm={groupForm}
      createEntityForm={createGroupForm}
      EditEntityFormComponent={LocationGroupForm}
      CreateEntityFormComponent={LocationGroupForm}
      dataState={dataState}
      SortableListProps={{
        defaultSortColumn: 0,
        actions: actions,
        columns: columns,
        defaultSortDirection: SORT_DIRECTION.ASC,
        onRowClick: hasRequiredAccessLevel(
          crudAccessLevel.edit,
          currentUserAppRoles,
        )
          ? dispatchInitializeEditLocationGroup
          : null,
        getKey: locationGroup => locationGroup.get("id"),
      }}
      EditModalProps={{
        submitText: "Save",
        title: "Edit Location Group",
        formTitleField: GROUP_FORM.NAME,
        onSubmit: dispatchEditGroupOnSubmit,
        FormProps: {
          visibleFields: Set(Object.values(GROUP_FORM)).delete(
            GROUP_FORM.FACILITY,
          ),
        },
        DialogProps: {
          maxWidth: "md",
        },
      }}
      CreateModalProps={{
        submitText: "Create",
        title: "Create Location Group",
        onSubmit: dispatchCreateLocationGroupOnSubmit,
        DialogProps: {
          maxWidth: "md",
        },
      }}
    />
  );
};
export default compose(
  connect(
    state => ({
      locationGroups: getLocationGroupsFromState(state),
      currentUserAppRoles: getCurrentUserAppRolesFromState(state),
      dataState: getSummarizedDataStatesFromState(
        state,
        ACTIONS.GET_GROUPS,
        ACTIONS.DELETE_GROUP,
        ACTIONS.GROUP_UNDELETE,
      ),
    }),
    {
      dispatchOnLoad: getGroups,
      dispatchGetFields: getFields,
      dispatchDeleteGroup: deleteGroup,
      dispatchInitializeEditGroup: initializeEditGroup,
      dispatchEditGroupOnSubmit: editLocationGroupOnSubmit,
      dispatchGetFacilities: getStationaryAndMobileFacilities,
      dispatchCreateLocationGroupOnSubmit: createLocationGroupOnSubmit,
      dispatchInitializeEditLocationGroup: initializeEditLocationGroup,
    },
  ),
)(SettingsLocationGroupsContainer);
